import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, TrendingUp, Clock } from 'lucide-react';
interface SlideAIOpportunityProps {
  onContinue: () => void;
}
export function SlideAIOpportunity({
  onContinue
}: SlideAIOpportunityProps) {
  const [stage, setStage] = useState(-1);
  const pillars = [{
    icon: <ShieldCheck className="w-7 h-7" data-id="element-127" />,
    title: 'Reduce the Erosion',
    desc: 'Continuous, data-driven engagement keeps HCPs connected to the brand — actively defending market share instead of letting it fade.',
    metric: 'Slow decline from ~25% to ~10% per year'
  }, {
    icon: <TrendingUp className="w-7 h-7" data-id="element-128" />,
    title: 'Increase Profitability',
    desc: 'AI-automated marketing operates at a fraction of the cost of traditional sales and agency models — dramatically widening the margin on every euro of legacy revenue.',
    metric: 'Marketing cost reduced by up to 80%'
  }, {
    icon: <Clock className="w-7 h-7" data-id="element-129" />,
    title: 'Prolong the Lifespan',
    desc: 'Lower costs + slower erosion means the gap between revenue and cost stays positive for years longer. Products stay on market — generating value — instead of being withdrawn prematurely.',
    metric: 'Years of additional profitable life'
  }];
  useEffect(() => {
    const timers = pillars.map((_, idx) => setTimeout(() => setStage(idx), 600 + idx * 800));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-130">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-5xl font-serif text-white mb-4" data-id="element-131">
        Our approach unlocks <span className="text-teal-400" data-id="element-132">three levers</span>
      </motion.h2>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.3
    }} className="text-xl text-white/50 mb-12 max-w-3xl" data-id="element-133">
        By replacing traditional marketing with AI-optimized engagement, we
        change the economics of legacy brands entirely.
      </motion.p>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch" data-id="element-134">
        {pillars.map((pillar, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: stage >= idx ? 1 : 0,
        y: stage >= idx ? 0 : 30
      }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-8 flex flex-col relative overflow-hidden" data-id="element-135">
            <div className="absolute top-6 right-6 w-8 h-8 rounded-full bg-teal-400/10 border border-teal-400/30 flex items-center justify-center" data-id="element-136">
              <span className="text-teal-400 font-bold text-sm" data-id="element-137">{idx + 1}</span>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center text-teal-400 mb-6" data-id="element-138">
              {pillar.icon}
            </div>
            <h3 className="text-2xl font-semibold text-white mb-4" data-id="element-139">
              {pillar.title}
            </h3>
            <p className="text-white/60 leading-relaxed flex-1" data-id="element-140">
              {pillar.desc}
            </p>
            <div className="mt-6 pt-5 border-t border-navy-600" data-id="element-141">
              <span className="text-teal-400 font-medium text-sm" data-id="element-142">
                {pillar.metric}
              </span>
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 10
    }} transition={{
      delay: 0.5
    }} className="mt-8 flex items-center justify-between" data-id="element-143">
        <p className="text-white/40 text-sm max-w-lg" data-id="element-144">
          Combined, these three effects compound — each euro of legacy revenue
          becomes more profitable and lasts longer.
        </p>
        <Button onClick={onContinue} data-id="element-145">Strategy</Button>
      </motion.div>
    </div>;
}